'use client';

import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DancerSection } from './dancer-section';
import { isRenderableMediaUrl } from './media-url';
import type { DancerMediaItem } from './types';

interface Props {
  documents: DancerMediaItem[];
}

/**
 * Resume / supporting documents — flat list of downloadable links. PDFs
 * and Word/Pages files uploaded as part of a dancer's profile (most of
 * which arrive from the Meteor migration mis-tagged as media_type='photo')
 * land here. We render filename + extension chip + open-in-new-tab; no
 * inline preview because non-PDF files can't render in-page and PDFs
 * need a heavier viewer than this section deserves.
 */
export function DancerDocuments({ documents }: Props) {
  const { t } = useTranslation();

  if (documents.length === 0) return null;

  const defaultLabel = t('dancer:publicView.documents.defaultLabel');

  return (
    <DancerSection
      eyebrow={t('dancer:publicView.documents.eyebrow')}
      title={t('dancer:publicView.documents.title')}
      count={documents.length}
    >
      <ul className="divide-foreground/10 divide-y">
        {documents.map((d) => {
          const href = d.signed_url ?? d.external_url ?? d.storage_path ?? null;
          // Same fix as the gallery: the cached anon loader emits same-origin
          // /api/media proxy URLs, which the old http-only guard disabled
          // (href="#"). isRenderableMediaUrl accepts those + http(s) and still
          // rejects a raw storage_path (no leading slash) / empty.
          const isFetchable = !!href && isRenderableMediaUrl(href);
          const label = d.file_name?.trim() || defaultLabel;
          const ext = extensionOf(d.file_name) ?? extensionOf(href ?? '') ?? '';

          return (
            <li key={d.id}>
              <a
                href={isFetchable ? href : '#'}
                aria-disabled={!isFetchable}
                target={isFetchable ? '_blank' : undefined}
                rel={isFetchable ? 'noopener noreferrer' : undefined}
                className="hover:bg-foreground/[0.02] group flex items-center justify-between gap-4 py-4 transition-colors"
              >
                <span className="flex min-w-0 items-center gap-4">
                  <FileText
                    aria-hidden="true"
                    className="text-muted-foreground/70 size-4 shrink-0"
                  />
                  <span className="text-foreground truncate text-sm leading-tight">
                    {label}
                  </span>
                </span>
                {ext && (
                  <span className="text-muted-foreground/70 font-mono text-[10px] tracking-[0.22em] uppercase tabular-nums">
                    {ext}
                  </span>
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </DancerSection>
  );
}

function extensionOf(s: string | null | undefined): string | null {
  if (!s) return null;
  const match = s.match(/\.([a-z0-9]{2,5})(?:$|\?|#)/i);
  return match ? match[1]!.toLowerCase() : null;
}
